import React, { useMemo } from "react";

import Table from "components/table/Table";
import DefaultTextCell from "components/cells/DefaultTextCell";

import TeamCell from "./cells/TeamCell";

import CheckIcon from "icons/CheckIcon";
import { useMediaSize } from "util/useMediaSize";
import { Gender, Meet } from "types";
import { getGenderOptions, getTeamOptions } from "util/options";
import { TeamPoints, getTeamPoints } from "util/teamScoring";
import round from "lodash/round";
import some from "lodash/some";
import sumBy from "lodash/sumBy";
import size from "lodash/size";
import get from "lodash/get";

type TeamData = {
  team: string;
  points: number;
  lifters: TeamPoints["lifters"];
  gender?: Gender;
};

// prettier-ignore
const columns = [
    {key: 'team', label: 'Team', width: 250, renderer: TeamCell},
    {key: 'place', label: 'Place', width: 85, renderer: DefaultTextCell},
    {key: 'points', label: "Points", width: 110, renderer: DefaultTextCell}
  ];

const getPlacingCount = (lifters: any, placing: number) => {
  return size(lifters?.filter((l: any) => l.place === placing));
};

const sortTeams = (a: TeamData, b: TeamData) => {
  if (b.points !== a.points) return b.points - a.points;

  for (let i = 0; i < 100; i++) {
    const aPlacingCount = getPlacingCount(a.lifters, i);
    const bPlacingCount = getPlacingCount(b.lifters, i);
    if (aPlacingCount !== bPlacingCount) {
      return bPlacingCount - aPlacingCount;
    }
  }

  return (
    sumBy(b.lifters, "formulaTieBreakerPoints") -
    sumBy(a.lifters, "formulaTieBreakerPoints")
  );
};

const TeamResultsTable = ({ meet }: { meet: Meet }) => {
  const [forecasted, setForecasted] = React.useState(false);

  const media = useMediaSize();

  const data = useMemo(() => {
    const teams = getTeamOptions(null, meet);
    if (meet.teamScoringDivideByGender !== false) {
      const genders = getGenderOptions(null, meet);
      let byGenderData: any = [];
      genders.forEach((gender) => {
        const teamData: TeamData[] = teams.map((team) => {
          const teamName = team.value ?? "";
          const teamPoints = getTeamPoints({
            meet,
            teamName: teamName ?? "",
            gender: gender.value,
            forecasted,
          });
          return {
            team: teamName,
            points: teamPoints.score,
            lifters: teamPoints.lifters,
            gender: gender.value,
          };
        });

        const teamsWithLifters = teamData.filter((t) => {
          return t.lifters.length;
        });
        const sortedTeams = teamsWithLifters.sort(sortTeams);

        const withPlacing = sortedTeams.map((team, index) => {
          return { ...team, place: index + 1, points: round(team.points, 3) };
        });

        if (some(withPlacing, (t) => t.lifters.length)) {
          byGenderData = byGenderData.concat([
            {
              row: "title",
              name:
                gender.value === "MALE"
                  ? "Men"
                  : gender.value === "FEMALE"
                  ? "Women"
                  : gender.label,
            },
            ...withPlacing,
          ]);
        }
      });

      const data = [{ row: "header" }, ...byGenderData];
      return data;
    }

    const teamData: TeamData[] = teams.map((team) => {
      const teamName = team.value;
      const teamPoints = getTeamPoints({
        meet,
        teamName: teamName ?? "",
        forecasted,
      });
      return {
        team: teamName ?? "",
        points: teamPoints.score,
        lifters: teamPoints.lifters,
      };
    });
    const sortedTeams = teamData.sort(sortTeams);
    const withPlacing = sortedTeams.map((team, index) => {
      return { ...team, place: index + 1, points: round(team.points, 3) };
    });
    const data = [{ row: "header" }, ...withPlacing];
    return data;
  }, [forecasted, meet]);

  const getCellLines = (index: number) => {
    const team = get(data, index, {});
    if ("row" in team && team.row === "title") {
      return 1.1;
    }

    return 0.85;
  };

  return (
    <div style={{ height: "100%" }}>
      <div className="util-row">
        <div>
          Team Results By{" "}
          {meet.teamScoringMethod === "BY_FORMULA" ? "Formula" : "Placing"}
        </div>
        <button
          className="forecasted"
          onClick={() => setForecasted((prev) => !prev)}
        >
          Forecasted&nbsp;{forecasted && <CheckIcon />}
        </button>
      </div>

      <div className="table-wrapper">
        <Table
          data={data}
          numberOfFixedLeftColumns={1}
          columns={columns}
          getCellLines={getCellLines}
          meet={meet}
          scrollToAlignment="center"
          media={media}
          padBottom
        />
      </div>
    </div>
  );
};

export default TeamResultsTable;
